<script setup lang="ts">
  import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { useTranslation } from '@/composables/useTranslation'
  import { nextTick, ref, watch } from 'vue'

  const props = defineProps<{
    open: boolean
    currentTitle?: string | null
    /**
     * Tailwind class string forwarded to the dialog content. Callers nested
     * inside a higher-stacking-context dialog (e.g. ``ChatHistoryModal`` at
     * ``z-[60]``) must lift this dialog above with e.g. ``z-[70]`` so the
     * input is not hidden behind the outer overlay.
     */
    contentClass?: string
    /** Tailwind class string forwarded to the dialog overlay. */
    overlayClass?: string
  }>()

  const emit = defineEmits<{
    (e: 'update:open', value: boolean): void
    (e: 'confirm', title: string): void
  }>()

  const { t } = useTranslation({
    en: {
      rename: 'Rename chat',
      chatTitle: 'Chat title',
      cancel: 'Cancel',
      save: 'Save'
    },
    de: {
      rename: 'Chat umbenennen',
      chatTitle: 'Chat-Titel',
      cancel: 'Abbrechen',
      save: 'Speichern'
    },
    fr: {
      rename: 'Renommer',
      chatTitle: 'Titre du chat',
      cancel: 'Annuler',
      save: 'Enregistrer'
    }
  })

  const renameValue = ref('')
  const inputRef = ref<HTMLInputElement | null>(null)

  watch(
    () => props.open,
    async (isOpen) => {
      if (isOpen) {
        renameValue.value = props.currentTitle || ''
        await nextTick()
        inputRef.value?.focus()
        inputRef.value?.select()
      }
    }
  )

  function confirm() {
    const trimmed = renameValue.value.trim()
    if (trimmed && trimmed !== props.currentTitle) {
      emit('confirm', trimmed)
    }
    emit('update:open', false)
  }
</script>

<template>
  <Dialog
    :open="open"
    @update:open="emit('update:open', $event)"
  >
    <DialogContent
      :class="['max-w-sm', contentClass]"
      :overlay-class="overlayClass"
    >
      <DialogHeader>
        <DialogTitle>{{ t('rename') }}</DialogTitle>
      </DialogHeader>

      <div class="flex flex-col gap-1.5">
        <label
          for="rename-input"
          class="text-sm font-medium text-gray-700"
        >
          {{ t('chatTitle') }}
        </label>
        <input
          id="rename-input"
          ref="inputRef"
          v-model="renameValue"
          type="text"
          class="w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-purple-500
            focus:outline-none focus:ring-1 focus:ring-purple-500"
          data-cy="rename-chat-input"
          @keydown.enter="confirm"
        />
      </div>

      <DialogFooter>
        <button
          type="button"
          class="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
          @click="emit('update:open', false)"
        >
          {{ t('cancel') }}
        </button>
        <button
          type="button"
          class="rounded-md bg-purple-600 px-4 py-2 text-sm font-medium text-white hover:bg-purple-700"
          data-cy="confirm-rename-button"
          @click="confirm"
        >
          {{ t('save') }}
        </button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
